import { posthog } from "@/instrument";

/**
 * Centralised PostHog event-name registry for the tenant app.
 *
 * Adding a new event requires THREE actions in the same change:
 *   1. Add the constant here. The TypeScript union derived below is the
 *      authoritative list — `captureEvent("foo")` with an unregistered
 *      string fails the build.
 *   2. Document it in `docs/architecture/posthog-tracking-strategy.md`
 *      under the right tier, with its safe-property contract.
 *   3. Wire the `captureEvent(AnalyticsEvents.X, props)` call at the
 *      mutation `onSuccess` / form-submit success / `useEffect` site.
 *
 * Naming convention: `domain_action[_qualifier]`, snake_case. Past
 * tense for completions (`transaction_created`), past tense + qualifier
 * for sub-steps (`transaction_cancel_step_completed`), past tense for
 * starts (`transaction_cancel_started`).
 */
export const AnalyticsEvents = {
  // Tier 1 — activation aha-moments
  TransactionCreated: "transaction_created",
  MemberCreated: "member_created",
  MemberUpdated: "member_updated",
  ActivityCreated: "activity_created",
  PledgeCreated: "pledge_created",
  BudgetCreated: "budget_created",
  InvitationAccepted: "invitation_accepted",
  // Modules that shipped after the first tracking pass and had no events at all: a church could build its
  // whole structure — departments, groups, programs, an asset register — and none of it reached PostHog.
  AssetCreated: "asset_created",
  GroupCreated: "group_created",
  DepartmentCreated: "department_created",
  ProgramCreated: "program_created",

  // Reports — the module was promoted out of Finance with zero instrumentation, so the catalog could not
  // answer the only questions it exists to answer: which reports get run, and which are never touched.
  // `template_id` / `report_id` are CATALOG KEYS (e.g. "trial-balance"), never a church's data.
  ReportCatalogViewed: "report_catalog_viewed",
  ReportRun: "report_run",
  ReportExported: "report_exported",

  // Finance investigation (IBA-75) — whether anyone actually walks the audit trail.
  InvestigationOpened: "investigation_opened",

  // Tier 2 — multi-step friction (drop-off funnels)
  TransactionCancelStarted: "transaction_cancel_started",
  TransactionCancelStepCompleted: "transaction_cancel_step_completed",
  TransactionCancelled: "transaction_cancelled",
  MemberImportStarted: "member_import_started",
  MemberImportStepCompleted: "member_import_step_completed",
  MemberImportCompleted: "member_import_completed",
  MemberImportFailed: "member_import_failed",
  MemberFormStepCompleted: "member_form_step_completed",

  // Tier 4 — manual navigation (deliberately small — 5 surfaces, NOT every route)
  DashboardViewed: "dashboard_viewed",
  FinanceOverviewViewed: "finance_overview_viewed",
  MembersListViewed: "members_list_viewed",
  ActivitiesViewed: "activities_viewed",
  TransactionsViewed: "transactions_viewed",
  WorkQueueViewed: "work_queue_viewed",

  // Announcements (tenant banner)
  // Properties are primitives only — IDs and enums. Never include the
  // message text, action label, or any HTML body content. See
  // `docs/architecture/posthog-tracking-strategy.md` for the safe-property
  // contract. (v1 follow-up: server-side `source` and `targetType` are not
  // exposed on the active DTO — track them in v1.1 once added.)
  AnnouncementImpression: "announcement_impression",
  AnnouncementDismissed: "announcement_dismissed",
  AnnouncementActionClicked: "announcement_action_clicked",
  AnnouncementSnoozed: "announcement_snoozed",
  AnnouncementAcknowledged: "announcement_acknowledged",

  // Rafiki (in-app assistant) — engagement + answer quality funnel.
  // Properties are primitives only — enums, counts, booleans, and catalog
  // route ids. NEVER include the question text, the answer text, sources
  // titles, workspace/branch slugs, or any user content.
  RafikiOpened: "rafiki_opened",
  RafikiMessageSent: "rafiki_message_sent",
  RafikiResponseFailed: "rafiki_response_failed",
  RafikiFeedbackSubmitted: "rafiki_feedback_submitted",
  RafikiRouteClicked: "rafiki_route_clicked",
  RafikiConversationCreated: "rafiki_conversation_created",
  RafikiConversationSwitched: "rafiki_conversation_switched",
  RafikiConversationDeleted: "rafiki_conversation_deleted",

  // Product feedback (in-app header button). Properties are primitives only —
  // the feedback type (enum) and optional rating (1–5). NEVER the message text.
  FeedbackSubmitted: "feedback_submitted",

  // Diagnostics — client-observed API latency (includes network + edge + Railway + backend). Paired with the
  // server-side `api_request_server` event: comparing this event's `duration_ms` to the server's `total_ms`
  // for the same `route` isolates network/infra time from backend time. Props are primitives only — the
  // route is a TEMPLATED pattern (`/departments/:id/finance`), never a concrete id-bearing path.
  ApiRequestClient: "api_request_client",

  // A user opened a feature's tutorial video. Tells us which screens people cannot work out on their own,
  // which is the whole reason the videos exist. Properties are primitives only — the feature key, the
  // provider, and the language actually served. NEVER the video URL or the operator's title.
  TutorialVideoOpened: "tutorial_video_opened",

  // Global search palette (desktop header, Cmd/Ctrl+K). Properties are primitives only: how it was opened
  // (`open_mode`: shortcut / click), and for an opened result its kind (`result_kind`, a SearchResultKind or
  // page / action) and how many rows the palette showed (`result_count`). NEVER the query text or the result.
  GlobalSearchOpened: "global_search_opened",
  GlobalSearchResultOpened: "global_search_result_opened",
} as const;

export type AnalyticsEventName = (typeof AnalyticsEvents)[keyof typeof AnalyticsEvents];

/**
 * Property contract — primitives only, no PII. Reviewer must enforce.
 *
 * Allowed key suffixes: `_id`, `_status`, `_type`, `_count`, `_step`,
 * `_format`, `_kind`, `_scope`, `_mode`. Values must be IDs, enums,
 * counts, or booleans — never names, emails, phones, addresses, DOBs,
 * amounts, notes, descriptions, or raw record contents.
 *
 * See `docs/architecture/posthog-tracking-strategy.md` for the full
 * PII exclusion list and the allowed-property matrix.
 */
export type AnalyticsEventProps = Record<string, string | number | boolean | null | undefined>;

/**
 * Send an event to PostHog. No-ops silently when PostHog wasn't
 * initialised (dev / preview / staging — see `apps/tenant/src/instrument.ts`
 * for the triple gate). Safe to call from anywhere — Node-style (no
 * hook required) so it works inside mutation callbacks, route loaders,
 * and renderless effects alike.
 */
export function captureEvent(name: AnalyticsEventName, props?: AnalyticsEventProps): void {
  // PostHog now initialises on staging too (so feature flags resolve pre-prod), but analytics capture stays
  // PRODUCTION-only. Without this guard, broadening the init in instrument.ts would leak events from staging.
  if (import.meta.env.VITE_APP_ENV !== "production") return;
  posthog?.capture(name, props);
}

/**
 * Collapse a concrete API path into a low-cardinality, PII-free route pattern for analytics: id-like segments
 * (cuid/uuid/numeric/long tokens) become `:id`. Keeps the endpoint groupable without ever emitting a real id.
 * Mirrors the server-side normalizer so client and server events line up on the same `route`.
 */
export function normalizeApiRoute(url: string): string {
  let path = url;
  try {
    // Handles absolute URLs and strips query/hash; falls back to the raw string for relative paths.
    path = new URL(url, "http://x").pathname;
  } catch {
    path = url.split(/[?#]/)[0] ?? url;
  }
  const idLike = /^(?:c[a-z0-9]{20,}|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|\d+|[a-zA-Z0-9_-]{21,})$/;
  const pattern = path
    .split("/")
    .map((segment) => (segment && idLike.test(segment) ? ":id" : segment))
    .join("/");
  return pattern.length > 120 ? pattern.slice(0, 120) : pattern || "/";
}

/** Fire-and-forget client-observed API latency (production-only via captureEvent). */
export function captureApiRequestPerf(input: { method: string; url: string; status: number; durationMs: number }): void {
  captureEvent(AnalyticsEvents.ApiRequestClient, {
    route: normalizeApiRoute(input.url),
    method: input.method.toUpperCase(),
    status: input.status,
    duration_ms: Math.round(input.durationMs),
    ok: input.status >= 200 && input.status < 400,
  });
}
